/**
 * Fixture locali del contratto C1 (piano Client sez. 4, F1): servono a
 * sviluppare la UI senza il backend SLA attivo, perche' le rotte `/sla/...`
 * non sono esercitabili in un ambiente di test (sez. 11.2 e 11.5 punto 5).
 * *(Rettifica 2026-09-20: il motivo **non** e' che le rotte non sono
 * committate — lo sono (`47816260`, `git diff HEAD` vuoto su
 * `Controller.SLA.pas`/`Service.SLA.pas`); il motivo e' che il Server e'
 * **non compilato** e la rotta non e' esposta in un ambiente di test.)*
 *
 * Sono dati con identificatori, revisioni e `as_of` espliciti, non screenshot:
 * un componente che le consuma deve poter ragionare su salute, revisioni e
 * scadenze reali. Ogni blocco dichiara quale regola del contratto rappresenta.
 *
 * Attivazione: `REACT_APP_SLA_FIXTURES=true` in `.env` (default: spento),
 * letta da `slaApi.ts`. Nessun componente deve importare le fixture per
 * decidere lo stato: le fixture sostituiscono solo il trasporto HTTP.
 */

import type {
  SlaDefinitionsResponse,
  SlaDiagramResponse,
  SlaInstancesResponse,
  SlaKpiResponse,
  SlaPreviewResponse,
  SlaSettingsDto,
  SlaUserPreferencesDto,
  SlaComplianceResponse,
  SlaDrilldownResponse,
  SlaModelBreakdownResponse,
  SlaTrendResponse,
} from "model/slaModel";

/** Identificatori usati da tutte le fixture: coerenti tra loro. */
export const SLA_FIXTURE_IDS = {
  idorganization: 2,
  idprocessmodel: 15,
  model_revision: 3,
  previous_model_revision: 2,
  idprocess: 4711,
  iduser: 88,
  idrole: 12,
} as const;

/** `as_of` fisso: le fixture restano deterministiche anche a orologio avanzato. */
const AS_OF = '2026-09-18T09:30:00Z';

/**
 * Definizioni modello/versione. Chiavi minuscole senza underscore: e' la forma
 * serializzata da DMVC con `[MVCNameCase(ncLowerCase)]` (vedi `slaModel.ts`).
 * Copre i casi escalation della sez. 11.1: ritardo NULL (nessuna escalation),
 * ruolo valorizzato e ruolo assente (ricaduta sul responsabile).
 *
 * Non copre "breach al livello 1 / a un livello successivo": quel dato non e'
 * nel payload istanza ne' in quello delle definizioni e il Client non deve
 * inventarlo (sez. 11.5 punto 2, decisione ancora aperta).
 */
export const SLA_FIXTURE_DEFINITIONS: SlaDefinitionsResponse = {
  definitions: [
    {
      id: 101,
      idorganization: SLA_FIXTURE_IDS.idorganization,
      idprocessmodel: SLA_FIXTURE_IDS.idprocessmodel,
      modelrevision: SLA_FIXTURE_IDS.model_revision,
      targettype: 1,
      targetdesignerid: null,
      durationformat: 1,
      durationvalue: 3,
      durationunit: 3,
      riskremainingpercent: 20,
      responsibleuserid: SLA_FIXTURE_IDS.iduser,
      responsibleroleid: null,
      escalationafterworkinghours: 8,
      escalationlevels: 3,
      escalationroleid: SLA_FIXTURE_IDS.idrole,
      active: true,
      updatedat: AS_OF,
      updatedby: SLA_FIXTURE_IDS.iduser,
    },
    {
      id: 102,
      idorganization: SLA_FIXTURE_IDS.idorganization,
      idprocessmodel: SLA_FIXTURE_IDS.idprocessmodel,
      modelrevision: SLA_FIXTURE_IDS.model_revision,
      targettype: 2,
      targetdesignerid: 'Activity_1_check',
      durationformat: 1,
      durationvalue: 4,
      durationunit: 2,
      riskremainingpercent: 25,
      responsibleuserid: SLA_FIXTURE_IDS.iduser,
      responsibleroleid: null,
      // Ruolo assente: si ricade sul responsabile configurato.
      escalationafterworkinghours: 4,
      escalationlevels: 2,
      escalationroleid: null,
      active: true,
      updatedat: AS_OF,
      updatedby: SLA_FIXTURE_IDS.iduser,
    },
    {
      id: 103,
      idorganization: SLA_FIXTURE_IDS.idorganization,
      idprocessmodel: SLA_FIXTURE_IDS.idprocessmodel,
      modelrevision: SLA_FIXTURE_IDS.model_revision,
      targettype: 2,
      targetdesignerid: 'Activity_2_approve',
      durationformat: 1,
      durationvalue: 2,
      durationunit: 2,
      riskremainingpercent: null,
      responsibleuserid: null,
      responsibleroleid: SLA_FIXTURE_IDS.idrole,
      // Ritardo NULL: nessuna escalation, anche se ruolo e livelli sono valorizzati.
      escalationafterworkinghours: null,
      escalationlevels: 5,
      escalationroleid: SLA_FIXTURE_IDS.idrole,
      active: true,
      updatedat: AS_OF,
      updatedby: SLA_FIXTURE_IDS.iduser,
    },
  ],
  meta: { model_revision: String(SLA_FIXTURE_IDS.model_revision) },
};

/** Esito della GET quando il modello non esiste, e' cancellato o e' di altro tenant. */
export const SLA_FIXTURE_MODEL_NOT_FOUND = {
  status: 404,
  code: 'sla_model_not_found',
} as const;

/** Conflitto di revisione: nessuna scrittura, la bozza del client resta valida. */
export const SLA_FIXTURE_REVISION_CONFLICT = {
  status: 409,
  code: 'sla_revision_conflict',
} as const;

/** Motivo mancante o payload incoerente sull'override. */
export const SLA_FIXTURE_REASON_REQUIRED = {
  status: 422,
  code: 'sla_reason_required',
} as const;

export const SLA_FIXTURE_PREVIEW_ESTIMABLE: SlaPreviewResponse = {
  estimable: true,
  as_of: AS_OF,
  deadline_at: '2026-09-29T16:00:00.000+02:00',
  risk_at: '2026-09-28T09:30:00.000+02:00',
  warnings: [],
};

/** Preview non stimabile: avviso strutturato, non un errore e non un path inventato. */
export const SLA_FIXTURE_PREVIEW_NOT_ESTIMABLE: SlaPreviewResponse = {
  estimable: false,
  as_of: AS_OF,
  deadline_at: '',
  risk_at: '',
  warnings: ['sla.validation.estimate_unavailable'],
};

/**
 * Istanze aperte: salute nei tre stati, una violazione gia' tracciata, un task
 * non preso in carico (`iduser = 0`), un override (`overridden = true`) con
 * originale e vigente divergenti. `cancelled`/`interrupted` non sono qui: le
 * istanze chiuse escono dalla lista e vivono nelle statistiche (vedi sotto).
 */
export const SLA_FIXTURE_INSTANCES: SlaInstancesResponse = {
  instances: [
    {
      id: 9001,
      idprocess: SLA_FIXTURE_IDS.idprocess,
      target_type: 2,
      target_designerid: 'Activity_1_check',
      revision: 1,
      health: 0,
      overridden: false,
      timezone_id: 'Europe/Rome',
      started_at: '2026-09-18T07:00:00Z',
      original_deadline_at: '2026-09-18T12:00:00Z',
      effective_deadline_at: '2026-09-18T12:00:00Z',
      risk_at: '2026-09-18T11:00:00Z',
      activity_description: 'Verifica documenti',
      process_description: 'Onboarding cliente',
      iduser: SLA_FIXTURE_IDS.iduser,
      idswimlane: 3,
      remaining_working_seconds: 7200,
      overdue_working_seconds: 0,
      clock_running: true,
      next_clock_change_at: '2026-09-18T11:00:00Z',
      escalation_level: 0,
      escalation_levels: 1,
      as_of: AS_OF,
    },
    {
      id: 9002,
      idprocess: SLA_FIXTURE_IDS.idprocess,
      target_type: 2,
      target_designerid: 'Activity_2_approve',
      revision: 1,
      health: 1,
      overridden: true,
      timezone_id: 'Europe/Rome',
      started_at: '2026-09-17T08:00:00Z',
      original_deadline_at: '2026-09-18T14:00:00Z',
      // Vigente prorogata: l'originale non e' stata riscritta.
      effective_deadline_at: '2026-09-19T10:00:00Z',
      risk_at: '2026-09-19T08:00:00Z',
      activity_description: 'Approvazione commerciale',
      process_description: 'Onboarding cliente',
      iduser: SLA_FIXTURE_IDS.iduser,
      idswimlane: 4,
      remaining_working_seconds: 1800,
      overdue_working_seconds: 0,
      clock_running: true,
      next_clock_change_at: '2026-09-18T11:00:00Z',
      escalation_level: 1,
      escalation_levels: 3,
      as_of: AS_OF,
    },
    {
      id: 9003,
      idprocess: SLA_FIXTURE_IDS.idprocess,
      target_type: 2,
      target_designerid: 'Activity_3_call',
      revision: 2,
      health: 2,
      overridden: false,
      timezone_id: 'Europe/Rome',
      started_at: '2026-09-16T09:00:00Z',
      original_deadline_at: '2026-09-17T15:00:00Z',
      effective_deadline_at: '2026-09-17T15:00:00Z',
      risk_at: '2026-09-17T13:00:00Z',
      activity_description: 'Chiamata di benvenuto',
      process_description: 'Onboarding cliente',
      iduser: SLA_FIXTURE_IDS.iduser,
      idswimlane: 3,
      remaining_working_seconds: 0,
      overdue_working_seconds: 5400,
      clock_running: true,
      next_clock_change_at: '2026-09-18T11:00:00Z',
      escalation_level: 2,
      escalation_levels: 3,
      as_of: AS_OF,
    },
    {
      id: 9004,
      idprocess: SLA_FIXTURE_IDS.idprocess,
      target_type: 2,
      target_designerid: 'Activity_4_review',
      revision: 1,
      health: 0,
      overridden: false,
      timezone_id: 'Europe/Rome',
      started_at: '2026-09-18T08:30:00Z',
      original_deadline_at: '2026-09-21T15:00:00Z',
      effective_deadline_at: '2026-09-21T15:00:00Z',
      risk_at: '2026-09-21T11:00:00Z',
      activity_description: 'Revisione legale',
      process_description: 'Onboarding cliente',
      // Task disponibile ma non ancora preso in carico.
      iduser: 0,
      idswimlane: 5,
      remaining_working_seconds: 21600,
      overdue_working_seconds: 0,
      clock_running: false,
      next_clock_change_at: '2026-09-19T06:00:00Z',
      escalation_level: 0,
      escalation_levels: 1,
      as_of: AS_OF,
    },
  ],
  pagination: {
    curr_page: '0',
    next_page_uri: '/flowentisysman/v1/sla/organizations/2/instances?page=1',
  },
};

export const SLA_FIXTURE_KPI: SlaKpiResponse = {
  kpi: { on_time: 12, at_risk: 3, breached: 2, total: 17, as_of: AS_OF },
};

/**
 * Diagramma per nodo. Include un nodo senza SLA (`has_sla = false`), un nodo
 * con istanze di revisione precedente ancora presenti (`other_revision_instances`),
 * un nodo senza istanze aperte (`worst_health = null`) e lo stato peggiore che
 * vince sul nodo (`breached`). Le istanze su nodi **rimossi** dal modello
 * corrente non sono rappresentabili in questo payload (sez. 11.3): il recupero
 * resta nella lista Monitor.
 */
export const SLA_FIXTURE_DIAGRAM: SlaDiagramResponse = {
  diagram: [
    {
      flownodetype: 1,
      idnode: 501,
      designerid: 'Activity_1_check',
      name: 'Verifica documenti',
      idswimlane: 3,
      has_sla: true,
      active: 2,
      on_time: 1,
      at_risk: 1,
      breached: 0,
      problematic: 1,
      worst_health: 1,
      other_revision_instances: 0,
      next_deadline_at: '2026-09-18T12:00:00Z',
      remaining_working_seconds: 7200,
      overdue_working_seconds: 0,
      clock_running: true,
      next_clock_change_at: '2026-09-18T11:00:00Z',
      as_of: AS_OF,
    },
    {
      flownodetype: 1,
      idnode: 502,
      designerid: 'Activity_2_approve',
      name: 'Approvazione commerciale',
      idswimlane: 4,
      has_sla: true,
      active: 1,
      on_time: 0,
      at_risk: 0,
      breached: 1,
      problematic: 1,
      worst_health: 2,
      other_revision_instances: 2,
      next_deadline_at: '2026-09-17T15:00:00Z',
      remaining_working_seconds: 0,
      overdue_working_seconds: 5400,
      clock_running: true,
      next_clock_change_at: '2026-09-18T11:00:00Z',
      as_of: AS_OF,
    },
    {
      flownodetype: 1,
      idnode: 503,
      designerid: 'Activity_3_call',
      name: 'Chiamata di benvenuto',
      idswimlane: 3,
      // Nodo del modello senza SLA: neutro, non verde implicito.
      has_sla: false,
      active: 0,
      on_time: 0,
      at_risk: 0,
      breached: 0,
      problematic: 0,
      worst_health: null,
      other_revision_instances: 0,
      next_deadline_at: null,
      remaining_working_seconds: null,
      overdue_working_seconds: null,
      clock_running: null,
      next_clock_change_at: null,
      as_of: AS_OF,
    },
    {
      flownodetype: 2,
      idnode: 504,
      designerid: 'Gateway_1_split',
      name: 'Split',
      idswimlane: 0,
      has_sla: false,
      active: 0,
      on_time: 0,
      at_risk: 0,
      breached: 0,
      problematic: 0,
      worst_health: null,
      other_revision_instances: 0,
      next_deadline_at: null,
      remaining_working_seconds: null,
      overdue_working_seconds: null,
      clock_running: null,
      next_clock_change_at: null,
      as_of: AS_OF,
    },
  ],
  meta: { model_revision: String(SLA_FIXTURE_IDS.model_revision) },
};

/** Diagramma chiesto su una revisione precedente del modello. */
export const SLA_FIXTURE_DIAGRAM_PREVIOUS_REVISION: SlaDiagramResponse = {
  ...SLA_FIXTURE_DIAGRAM,
  meta: { model_revision: String(SLA_FIXTURE_IDS.previous_model_revision) },
};

export const SLA_FIXTURE_SETTINGS: SlaSettingsDto = {
  idorganization: SLA_FIXTURE_IDS.idorganization,
  enabled: true,
  riskremainingpercent: 20,
  nudgeremainingpercent: 50,
  escalationenabled: true,
  digestenabled: true,
  digestat: '09:00:00',
  mindurationminutes: 30,
  maxnotificationlatencyseconds: 60,
  updatedat: AS_OF,
  updatedby: SLA_FIXTURE_IDS.iduser,
};

/**
 * Gate di organizzazione spento: le definizioni con escalation sopra restano
 * **inerti** finche' l'organizzazione non riattiva `escalation_enabled`
 * (sez. 11.1, doppio gate). Serve a distinguere "configurato" da "attivo" senza
 * dedurre l'uno dall'altro; la scelta di come mostrarlo (campi disabilitati o
 * avviso) resta la domanda aperta 11.5 punto 4.
 */
export const SLA_FIXTURE_SETTINGS_ESCALATION_DISABLED: SlaSettingsDto = {
  ...SLA_FIXTURE_SETTINGS,
  escalationenabled: false,
};

export const SLA_FIXTURE_PREFERENCES: SlaUserPreferencesDto = {
  iduser: SLA_FIXTURE_IDS.iduser,
  idorganization: SLA_FIXTURE_IDS.idorganization,
  digestenabled: false,
  escalationenabled: true,
  transactionalenabled: true,
  greetingdismissedat: null,
  updatedat: AS_OF,
};

/**
 * Compliance lato Python P2 (contratto reale: `backend_statistiche/app/schemas/sla.py:102-114`):
 * due basi original/effective con denominatori separati, i **tre** esiti esclusi
 * (`cancelled`/`interrupted`/`authority_terminated`) fuori dal denominatore,
 * copertura e tempo medio, `sample` e le `message_keys` del catalogo P1.
 *
 * Il valore di `effective` **diverge** da `original` (9/10 contro 8/10) e le
 * `message_keys` sono quelle di `services/sla/statistics.py:92-108`: cosi' una
 * superficie che scambiasse le due basi o inventasse prosa non combacerebbe col
 * payload. Nessun doppio conteggio per un breach escalato: gli esiti sono
 * `completion_kind` di `sla_result`, non livelli raggiunti.
 */
/**
 * Blocco `message_keys` del contratto P2, condiviso dalle tre superfici
 * Statistiche (conformita', trend, drill-down): il Server manda le **stesse**
 * chiavi del catalogo P1 per tutte (`services/sla/statistics.py:92-108`,
 * `_message_keys()`). Trascritte, non inventate.
 */
export const SLA_FIXTURE_MESSAGE_KEYS: Record<string, string> = {
  title: 'sla.statistics.compliance.title',
  rate: 'sla.statistics.compliance.rate',
  rate_unavailable: 'sla.statistics.compliance.rate_unavailable',
  note: 'sla.statistics.compliance.note',
  coverage: 'sla.statistics.coverage',
  average_working_time: 'sla.statistics.average_working_time',
  privacy: 'sla.statistics.privacy_note',
  period_day: 'sla.statistics.period.day',
  period_week: 'sla.statistics.period.week',
  period_timezone: 'sla.statistics.period.timezone',
  period_limit: 'sla.statistics.period.limit',
  drilldown_title: 'sla.statistics.drilldown.title',
  drilldown_model_version: 'sla.statistics.drilldown.model_version',
  drilldown_no_double_count: 'sla.statistics.drilldown.no_double_count',
  load_more: 'sla.statistics.load_more',
};

export const SLA_FIXTURE_COMPLIANCE: SlaComplianceResponse = {
  level: 'process',
  period: { from: '2026-09-01', to: '2026-09-18' },
  original: { met: 8, eligible: 10, percent: 80 },
  effective: { met: 9, eligible: 10, percent: 90 },
  excluded: { cancelled: 1, interrupted: 1, authority_terminated: 0 },
  without_sla: 3,
  coverage_percent: 80,
  average_working_seconds: 5400.5,
  sample: { rows: 15, limit: null, limited: false },
  timezone: 'Europe/Rome',
  as_of: AS_OF,
  message_keys: SLA_FIXTURE_MESSAGE_KEYS,
};

/**
 * Zero campione: percentuale N/D, non 0%. La copertura e il tempo medio hanno la
 * stessa regola (`services/sla/statistics.py:221-235`): `null` con totale o
 * denominatore zero. Gli esiti esclusi restano esposti e valgono 0.
 */
export const SLA_FIXTURE_COMPLIANCE_NO_SAMPLE: SlaComplianceResponse = {
  ...SLA_FIXTURE_COMPLIANCE,
  original: { met: 0, eligible: 0, percent: null },
  effective: { met: 0, eligible: 0, percent: null },
  excluded: { cancelled: 0, interrupted: 0, authority_terminated: 0 },
  without_sla: 14,
  coverage_percent: null,
  average_working_seconds: null,
  sample: { rows: 14, limit: null, limited: false },
};

/**
 * Trend lato Python P2 (`schemas/sla.py:117-133`): punti per giorno locale, con
 * **entrambe** le basi e gli stessi conteggi della conformita'. I due bucket
 * sommano esattamente i conteggi di `SLA_FIXTURE_COMPLIANCE` (8/10 e 9/10), cosi'
 * una superficie che cambiasse base fra le viste non combacerebbe col payload.
 *
 * `bucket` e' l'identita' del punto (giorno locale risolto in SQL,
 * `queries.py:73-79`): due letture della stessa serie rendono gli stessi bucket.
 * L'`effective` del secondo giorno **diverge** dall'`original` (5/6 contro 4/6):
 * un punto che mostrasse una sola base sarebbe visibile.
 */
export const SLA_FIXTURE_TREND: SlaTrendResponse = {
  level: 'process',
  granularity: 'day',
  period: { from: '2026-09-01', to: '2026-09-18' },
  timezone: 'Europe/Rome',
  as_of: AS_OF,
  // `limit` = tetto dei bucket (400), `limited` = serie troncata da quel tetto.
  sample: { rows: 15, limit: 400, limited: false },
  data: [
    {
      bucket: '2026-09-02',
      original: { met: 4, eligible: 4, percent: 100 },
      effective: { met: 4, eligible: 4, percent: 100 },
      excluded: { cancelled: 1, interrupted: 0, authority_terminated: 0 },
      without_sla: 2,
    },
    {
      bucket: '2026-09-03',
      original: { met: 4, eligible: 6, percent: 66.67 },
      effective: { met: 5, eligible: 6, percent: 83.33 },
      excluded: { cancelled: 0, interrupted: 1, authority_terminated: 0 },
      without_sla: 1,
    },
  ],
  message_keys: SLA_FIXTURE_MESSAGE_KEYS,
};

/**
 * Drill-down lato Python P2 (`schemas/sla.py:136-181`): una riga per **risultato
 * finale** (`sla_result.idinstance`, `UNIQUE`), quindi un breach escalato piu'
 * volte resta **una** riga e **un** breach. `completion_kind` 1 = completamento
 * valido, 3 = interrotto (senza `original_met`/`effective_met`, vincolo
 * `sla_result_met_only_when_completed_ck`); nessuna riga porta utente o ruolo.
 *
 * `customer_value` e' la dimensione storica del filtro Cliente, risolta alla
 * chiusura (`20260916_sla_core.sql:237-239`).
 */
export const SLA_FIXTURE_DRILLDOWN: SlaDrilldownResponse = {
  level: 'process',
  period: { from: '2026-09-01', to: '2026-09-18' },
  timezone: 'Europe/Rome',
  as_of: AS_OF,
  // `limit` = `page_size`; `limited` = esistono altre pagine.
  sample: { rows: 2, limit: 50, limited: false },
  data: [
    {
      sla_instance_id: 9001,
      process_id: SLA_FIXTURE_IDS.idprocess,
      target_type: 1,
      target_designer_id: 'Activity_1',
      target_node_id: 4711,
      closed_at: '2026-09-02T15:10:00Z',
      completion_kind: 1,
      eligible: true,
      original_met: false,
      effective_met: false,
      original_deadline_at: '2026-09-02T14:00:00Z',
      effective_deadline_at: '2026-09-02T14:00:00Z',
      consumed_working_seconds: 9000,
      customer_value: 'ACME',
      team_id: 3,
    },
    {
      sla_instance_id: 9002,
      process_id: SLA_FIXTURE_IDS.idprocess,
      target_type: 1,
      target_designer_id: 'Activity_2',
      target_node_id: 4712,
      closed_at: '2026-09-03T09:00:00Z',
      completion_kind: 3,
      eligible: true,
      original_met: null,
      effective_met: null,
      original_deadline_at: '2026-09-03T08:00:00Z',
      effective_deadline_at: '2026-09-03T08:00:00Z',
      consumed_working_seconds: 3600,
      customer_value: null,
      team_id: null,
    },
  ],
  pagination: { page: 1, page_size: 50, total_items: 2, total_pages: 1 },
  message_keys: SLA_FIXTURE_MESSAGE_KEYS,
};

/**
 * Ripartizione per modello (`GET /sla/{org}/breakdown/model`): i gruppi sommano
 * esattamente la conformita' fixture (10 eleggibili, 8 originali, 9 effettivi).
 */
export const SLA_FIXTURE_MODEL_BREAKDOWN: SlaModelBreakdownResponse = {
  level: 'process',
  period: { from: '2026-09-01', to: '2026-09-18' },
  timezone: 'Europe/Rome',
  as_of: AS_OF,
  sample: { rows: 15, limit: 500, limited: false },
  data: [
    {
      process_model_id: SLA_FIXTURE_IDS.idprocessmodel,
      name: 'Richiesta ferie',
      original: { met: 6, eligible: 7, percent: 85.71 },
      effective: { met: 7, eligible: 7, percent: 100 },
      missed: 0,
      excluded: { cancelled: 1, interrupted: 0, authority_terminated: 0 },
      without_sla: 3,
      average_working_seconds: 5400,
    },
    {
      process_model_id: 16,
      name: 'Onboarding cliente',
      original: { met: 2, eligible: 3, percent: 66.67 },
      effective: { met: 2, eligible: 3, percent: 66.67 },
      missed: 1,
      excluded: { cancelled: 0, interrupted: 1, authority_terminated: 0 },
      without_sla: 0,
      average_working_seconds: 9000,
    },
  ],
  message_keys: SLA_FIXTURE_MESSAGE_KEYS,
};
